/**
 * DeviceInfoPanel
 * ---------------
 * A user-facing, production-ready card grid that shows the same device data
 * the analytics pipeline captures, with a refresh button and a privacy note.
 *
 * • Async + feature-detected (every API is probed first)
 * • Graceful fallbacks when blocked/unsupported (Battery, UA-CH, Geolocation)
 * • Sends a `device_info_view` event to the backend on each render so visits
 *   are traceable (same schema, no API changes needed)
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import {
  RefreshCw, Cpu, HardDrive, Monitor, Battery, BatteryCharging,
  Smartphone, Laptop, Tablet, Wifi, Shield, Languages, Fingerprint, Zap,
} from 'lucide-react'
import { gatherDeepContext } from '../lib/fingerprint'
import { trackEvent } from '../lib/tracking'

// ------------------------------------------------------------
// Types
// ------------------------------------------------------------
type DeviceInfo = {
  browser_name?: string
  browser_version?: string
  browser_source: 'ua-client-hints' | 'user-agent'
  platform?: string
  platform_version?: string
  os_family?: string
  device_type?: 'mobile' | 'tablet' | 'desktop'
  device_model?: string
  architecture?: string
  bitness?: string
  ua_full_version_list?: string
  ram_gb?: number | 'unknown'
  cpu_cores?: number | 'unknown'
  screen?: string
  pixel_ratio?: number
  color_depth?: number
  language?: string
  languages?: string[]
  timezone?: string
  timezone_offset?: number
  battery_supported: boolean
  battery_level_pct?: number
  battery_charging?: boolean
  battery_remaining?: string
  max_touch_points?: number
  cookies_enabled?: boolean
  webdriver?: boolean
  do_not_track?: string
  gpu_renderer?: string
  gpu_vendor?: string
  online?: boolean
  network_type?: string
  user_agent?: string
}

// ------------------------------------------------------------
// Collector — async with full feature detection
// ------------------------------------------------------------
async function collectDeviceInfo(): Promise<DeviceInfo> {
  const info: DeviceInfo = { browser_source: 'user-agent', battery_supported: false }

  // 1. Browser via UA Client Hints (Chromium) → fallback to userAgent
  try {
    const uaData: any = (navigator as any).userAgentData
    if (uaData?.getHighEntropyValues) {
      const hints = await uaData.getHighEntropyValues([
        'architecture', 'bitness', 'model', 'platform', 'platformVersion',
        'uaFullVersion', 'fullVersionList',
      ])
      const brand = (hints.fullVersionList || uaData.brands || [])
        .find((b: any) => !/not.a.brand/i.test(b.brand)) || uaData.brands?.[0]
      info.browser_name = brand?.brand
      info.browser_version = brand?.version
      info.browser_source = 'ua-client-hints'
      info.platform = hints.platform
      info.platform_version = hints.platformVersion
      info.device_model = hints.model || undefined
      info.architecture = hints.architecture
      info.bitness = hints.bitness
      info.ua_full_version_list = (hints.fullVersionList || [])
        .map((b: any) => `${b.brand} ${b.version}`).join(', ')
    }
  } catch {/* ignore */}

  // Fallback browser parse from UA
  if (!info.browser_name) {
    const ua = navigator.userAgent
    const match =
      /(Edg|Edge|OPR|Opera|Chrome|Firefox|Safari|MSIE|Trident)\/?([\d.]+)/.exec(ua)
    if (match) {
      info.browser_name = match[1] === 'OPR' ? 'Opera'
        : match[1] === 'Edg' ? 'Edge'
        : match[1]
      info.browser_version = match[2]
    }
  }

  // 2. OS family + device type (reuse deep fingerprint cache)
  const deep = await gatherDeepContext()
  info.os_family = deep.os_family
  info.gpu_renderer = deep.gpu_renderer
  info.gpu_vendor = deep.gpu_vendor
  info.network_type = (navigator as any).connection?.effectiveType

  info.device_type =
    deep.os_family === 'android' || deep.os_family === 'ios'
      ? (matchMedia('(max-width: 900px) and (orientation: portrait)').matches ? 'mobile' : 'tablet')
      : 'desktop'

  if (!info.platform) info.platform = navigator.platform

  // 3. RAM + CPU
  info.ram_gb = (navigator as any).deviceMemory || 'unknown'
  info.cpu_cores = navigator.hardwareConcurrency || 'unknown'

  // 4. Display
  info.screen = `${screen.width} × ${screen.height}`
  info.pixel_ratio = window.devicePixelRatio
  info.color_depth = screen.colorDepth

  // 5. Locale
  info.language = navigator.language
  info.languages = [...(navigator.languages || [])]
  info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  info.timezone_offset = new Date().getTimezoneOffset()

  // 6. Battery — feature-detect, handle rejection
  try {
    const getBattery = (navigator as any).getBattery
    if (typeof getBattery === 'function') {
      const b: any = await getBattery.call(navigator)
      info.battery_supported = true
      info.battery_level_pct = Math.round((b.level ?? 0) * 100)
      info.battery_charging = !!b.charging
      if (b.charging && Number.isFinite(b.chargingTime) && b.chargingTime > 0) {
        info.battery_remaining = `${Math.round(b.chargingTime / 60)} min until full`
      } else if (!b.charging && Number.isFinite(b.dischargingTime) && b.dischargingTime > 0) {
        const h = Math.floor(b.dischargingTime / 3600)
        const m = Math.round((b.dischargingTime % 3600) / 60)
        info.battery_remaining = `${h}h ${m}m remaining`
      }
    }
  } catch {/* blocked — leave battery_supported false */}

  // 7. Misc capabilities
  info.max_touch_points = navigator.maxTouchPoints || 0
  info.cookies_enabled = navigator.cookieEnabled
  info.webdriver = !!(navigator as any).webdriver
  info.do_not_track = navigator.doNotTrack || 'unspecified'
  info.online = navigator.onLine
  info.user_agent = navigator.userAgent

  return info
}

// ------------------------------------------------------------
// UI
// ------------------------------------------------------------
export default function DeviceInfoPanel() {
  const [info, setInfo] = useState<DeviceInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshedAt, setRefreshedAt] = useState<Date>(new Date())

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const d = await collectDeviceInfo()
      setInfo(d)
      setRefreshedAt(new Date())
      // Ship to backend analytics (non-blocking)
      trackEvent('device_info_view', {
        browser: `${d.browser_name} ${d.browser_version}`,
        os: `${d.os_family} ${d.platform_version || ''}`.trim(),
        device_type: d.device_type,
        device_model: d.device_model,
        battery: d.battery_supported ? `${d.battery_level_pct}%` : 'unsupported',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const DeviceIcon = useMemo(() => {
    if (!info) return Laptop
    if (info.device_type === 'mobile') return Smartphone
    if (info.device_type === 'tablet') return Tablet
    return Laptop
  }, [info])

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="bg-gradient-cosmic bg-clip-text text-4xl font-bold text-transparent">
            Your Device
          </h1>
          <p className="mt-2 text-sm text-star-white/60">
            What your browser tells us about this device &amp; connection.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="group flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium hover:bg-white/10 transition disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 transition ${loading ? 'animate-spin' : 'group-hover:rotate-90'}`} />
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Cards */}
      {info && (
        <motion.div
          layout
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <Card icon={DeviceIcon} title="Device">
            <Row label="Type" value={cap(info.device_type)} />
            <Row label="OS" value={`${cap(info.os_family)} ${info.platform_version || ''}`.trim()} />
            <Row label="Model" value={info.device_model || 'Hidden by browser'} dim={!info.device_model} />
            <Row label="Architecture" value={info.architecture ? `${info.architecture} ${info.bitness}bit` : 'Unknown'} />
          </Card>

          <Card icon={Monitor} title="Browser">
            <Row label="Name" value={`${info.browser_name || '?'} ${info.browser_version || ''}`} />
            <Row label="Source" value={info.browser_source} mono />
            <Row label="Webdriver" value={info.webdriver ? 'Yes (bot?)' : 'No'} danger={info.webdriver} />
            <Row label="Do Not Track" value={info.do_not_track || '-'} />
          </Card>

          <Card icon={Cpu} title="Hardware">
            <Row label="CPU cores" value={String(info.cpu_cores)} />
            <Row label="RAM" value={info.ram_gb === 'unknown' ? 'Unknown' : `≈ ${info.ram_gb} GB`} />
            <Row label="Touch points" value={String(info.max_touch_points)} />
          </Card>

          <Card icon={Zap} title="Graphics">
            <Row label="Vendor" value={info.gpu_vendor || 'Hidden'} dim={!info.gpu_vendor} />
            <Row label="Renderer" value={info.gpu_renderer || 'Hidden'} dim={!info.gpu_renderer} />
          </Card>

          <Card icon={HardDrive} title="Display">
            <Row label="Screen" value={info.screen || '-'} />
            <Row label="Pixel ratio" value={String(info.pixel_ratio)} />
            <Row label="Color depth" value={`${info.color_depth}-bit`} />
          </Card>

          <Card
            icon={info.battery_charging ? BatteryCharging : Battery}
            title="Battery"
            accent={info.battery_charging ? 'emerald' : info.battery_level_pct != null && info.battery_level_pct < 20 ? 'red' : undefined}
          >
            {info.battery_supported ? (
              <>
                <Row
                  label="Level"
                  value={`${info.battery_level_pct}% ${info.battery_charging ? '(Charging)' : ''}`}
                />
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/5">
                  <div
                    className={`h-full transition-all ${
                      info.battery_charging
                        ? 'bg-emerald-500'
                        : (info.battery_level_pct ?? 0) < 20
                        ? 'bg-red-500'
                        : 'bg-gradient-cosmic'
                    }`}
                    style={{ width: `${info.battery_level_pct}%` }}
                  />
                </div>
                {info.battery_remaining && (
                  <p className="mt-2 text-xs text-star-white/60">{info.battery_remaining}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-star-white/50">
                Battery API not available (Safari, Firefox, or blocked).
              </p>
            )}
          </Card>

          <Card icon={Languages} title="Locale">
            <Row label="Language" value={info.language || '-'} />
            <Row label="All languages" value={info.languages?.join(', ') || '-'} small />
            <Row label="Timezone" value={info.timezone || '-'} />
            <Row label="UTC offset" value={`${-(info.timezone_offset || 0) / 60}h`} />
          </Card>

          <Card icon={Wifi} title="Network">
            <Row label="Online" value={info.online ? 'Yes' : 'No'} />
            <Row label="Connection" value={info.network_type || 'unknown'} />
          </Card>

          <Card icon={Shield} title="Privacy">
            <Row label="Cookies" value={info.cookies_enabled ? 'Enabled' : 'Disabled'} />
            <Row label="Do Not Track" value={info.do_not_track || '-'} />
          </Card>
        </motion.div>
      )}

      {/* User-agent (full) */}
      {info && (
        <details className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm">
          <summary className="cursor-pointer select-none text-star-white/70">
            <Fingerprint className="mr-2 inline h-4 w-4" />
            Raw User-Agent
          </summary>
          <p className="mt-3 break-all font-mono text-xs text-star-white/70">{info.user_agent}</p>
        </details>
      )}

      {/* Privacy note */}
      <div className="mt-8 flex items-start gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm">
        <Shield className="h-5 w-5 shrink-0 text-emerald-400" />
        <p className="text-emerald-100/80">
          <strong className="text-emerald-300">Privacy note:</strong>{' '}
          This information is collected only for improving your experience, debugging issues, and protecting
          against abuse. We never sell it. Last refreshed {refreshedAt.toLocaleTimeString()}.
        </p>
      </div>
    </div>
  )
}

// ------------------------------------------------------------
// Subcomponents
// ------------------------------------------------------------
function Card({
  icon: Icon,
  title,
  children,
  accent,
}: {
  icon: any
  title: string
  children: React.ReactNode
  accent?: 'emerald' | 'red'
}) {
  const ringClass =
    accent === 'emerald'
      ? 'ring-1 ring-emerald-500/30'
      : accent === 'red'
      ? 'ring-1 ring-red-500/30'
      : ''
  return (
    <motion.div
      layout
      whileHover={{ y: -2 }}
      className={`rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur transition ${ringClass}`}
    >
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5">
          <Icon className="h-4 w-4 text-star-white/80" />
        </div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-star-white/60">{title}</h3>
      </div>
      <div className="space-y-2 text-sm">{children}</div>
    </motion.div>
  )
}

function Row({
  label,
  value,
  mono,
  small,
  dim,
  danger,
}: {
  label: string
  value: string
  mono?: boolean
  small?: boolean
  dim?: boolean
  danger?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-xs text-star-white/50">{label}</span>
      <span
        className={[
          'text-right break-all',
          mono ? 'font-mono text-[11px]' : '',
          small ? 'text-xs' : '',
          dim ? 'text-star-white/40' : 'text-star-white',
          danger ? 'text-red-400' : '',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  )
}

function cap(s?: string) {
  if (!s) return '-'
  return s.charAt(0).toUpperCase() + s.slice(1)
}
